import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { CheckCircle, XCircle, AlertTriangle, Shield, ChevronDown, ChevronUp } from "lucide-react";

const Navigation = () => {
  return (
    <nav className="dark-header">
      <a href="/">
        <img src="/logos/logo-v5.jpg" alt="ARCANA" className="dark-logo-nav" />
      </a>
      <div className="dark-nav">
        <a href="/" className="dark-nav-link">HOME</a>
        <a href="/resources" className="dark-nav-link">RESOURCES</a>
        <a href="/services" className="dark-nav-link">SERVICES</a>
        <a href="/faq" className="dark-nav-link">FAQ</a>
        <button className="btn-primary" onClick={() => window.location.href = '/#assessment'}>
          FREE ASSESSMENT
        </button>
      </div>
      <button className="mobile-menu-btn">
        <span style={{color: 'var(--oro-arcana)'}}>☰</span>
      </button>
    </nav>
  );
};

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-accordion-item ${isOpen ? 'active' : ''}`}>
      <button className="faq-accordion-button" onClick={() => setIsOpen(!isOpen)}>
        <span className="faq-accordion-question">{question}</span>
        {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
      </button>
      {isOpen && (
        <div className="faq-accordion-content">
          <p className="faq-answer">{answer}</p>
        </div>
      )}
    </div>
  );
};

const SeedPhraseTyposWrongOrder = () => {
  return (
    <div className="App">
      <Helmet>
        <title>Seed Phrase Typos or Wrong Order: Safe Recovery Guide</title>
        <meta name="description" content="Seed phrase not working due to typos or wrong order? Learn safe checks, common mistakes, and when recovery is feasible. Non-custodial process. Free assessment." />
        <meta name="keywords" content="seed phrase typos, seed phrase wrong order, BIP39 typo recovery, seed word order wrong, crypto wallet not working" />
        <link rel="canonical" href="https://arcana-crypto.com/seed-phrase-typos-wrong-order.html" />
      </Helmet>

      <Navigation />
      
      <article className="section" style={{marginTop: '128px', minHeight: '100vh'}}>
        <div className="section-container" style={{maxWidth: '900px'}}>
          
          {/* Header */}
          <header>
            <h1 className="section-title" style={{fontSize: '42px', textAlign: 'left', marginBottom: '16px'}}>
              Seed Phrase Typos or Wrong Order: A Safe Recovery Approach
            </h1>
            <p className="section-intro" style={{fontSize: '18px', marginBottom: '40px'}}>
              When a <strong>seed phrase fails</strong>, the cause is often simple: a <strong>typo</strong>, 
              a wrong word, or words in the <strong>wrong order</strong>. The risk is not just losing time—it 
              is exposing sensitive data through unsafe recovery attempts.
            </p>
          </header>

          {/* Key Takeaways */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              Key Takeaways
            </h2>
            <ul className="column-list">
              <li>Most seed failures are typos, language mismatch, or word order issues.</li>
              <li>Random re-ordering attempts can explode in complexity.</li>
              <li>Never paste seeds into websites or unknown software.</li>
              <li>A structured approach can narrow candidates safely.</li>
              <li>Non-custodial recovery focuses on verification and controlled testing.</li>
            </ul>
          </section>

          {/* What to Do */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <CheckCircle size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              What to Do
            </h2>
            <div className="about-card">
              <ul className="column-list">
                <li>Check each word against the correct <strong>BIP39 wordlist</strong>.</li>
                <li>Look for common <strong>typo patterns</strong> (transposed letters, missing letters, autocorrect).</li>
                <li>Confirm word count and spacing.</li>
                <li>Identify the wallet and network.</li>
                <li>Gather safe hints (public address, xpub, txids).</li>
              </ul>
            </div>
          </section>

          {/* What to Avoid */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <XCircle size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              What to Avoid
            </h2>
            <div className="about-card">
              <ul className="column-list">
                <li>Do not brute-force manually.</li>
                <li>Avoid "seed checker" websites.</li>
                <li>Do not mix languages.</li>
                <li>Do not store the phrase in cloud notes.</li>
              </ul>
            </div>
          </section>

          {/* Technical Explanation */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              Technical Explanation (Simplified)
            </h2>
            <div className="legal-content">
              <p>
                <strong>BIP39 phrases</strong> validate via checksum. A <strong>typo</strong> can create a non-existent word 
                or a valid-but-wrong word. <strong>Wrong order</strong> is harder: even if all words are correct, 
                permutations grow extremely fast.
              </p>
              <p style={{marginTop: '16px'}}>
                For a 12-word seed with all words correct but <strong>wrong order</strong>, there are 479 million 
                possible combinations. For 24 words, the number becomes astronomically high. This is why 
                <strong> controlled computational approaches</strong> and pattern analysis are essential for feasibility.
              </p>
              <p style={{marginTop: '16px'}}>
                Our <strong>non-custodial</strong> methodology focuses on checksum validation, language verification, 
                and systematic candidate testing without ever taking control of client funds.
              </p>
            </div>
          </section>

          {/* Non-Custodial & Privacy */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <Shield size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              Non-Custodial & Privacy
            </h2>
            <div className="about-card">
              <p className="legal-content" style={{marginBottom: '16px'}}>
                Non-custodial, offline work, encrypted comms, data minimisation and deletion aligned 
                with GDPR principles.
              </p>
              <ul className="column-list">
                <li>Strictly non-custodial: we do not take custody of funds</li>
                <li>Offline processing with encrypted communications</li>
                <li>Data minimisation and secure deletion after completion</li>
                <li>GDPR compliant (UK-based operations)</li>
              </ul>
            </div>
          </section>

          {/* When Recovery is Feasible */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              When Recovery is Feasible
            </h2>
            <div className="about-card">
              <p className="legal-content">
                <strong>More feasible</strong> when only 1–2 words are uncertain, you know the wallet and language, 
                and you can provide an address/xpub for validation.
              </p>
              <p className="legal-content" style={{marginTop: '16px'}}>
                <strong>Less feasible (or more expensive)</strong> when multiple words are uncertain, 
                language is unknown, order is completely random, or no validation hints available.
              </p>
            </div>
          </section>

          {/* What We Need */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              What We Need for Assessment
            </h2>
            <div className="pricing-notes">
              <ul className="column-list">
                <li>Network and wallet type</li>
                <li>Seed length (12/18/24 words)</li>
                <li>What you suspect (typo vs order vs both)</li>
                <li>BIP39 language (best guess)</li>
                <li>Any address/xpub/txid for validation</li>
                <li>Time sensitivity</li>
              </ul>
            </div>
          </section>

          {/* FAQ */}
          <section style={{marginBottom: '80px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '32px'}}>
              Frequently Asked Questions
            </h2>
            <div className="faq-accordion">
              <FAQItem 
                question="If all words are valid, why does it fail?"
                answer="Order mismatch, language mismatch, or an additional passphrase (25th word) may be the cause."
              />
              <FAQItem 
                question="Can you fix autocorrect mistakes?"
                answer="Often yes, if uncertainty is limited to 1-2 words and you can provide validation hints."
              />
              <FAQItem 
                question="Do you need my full seed?"
                answer="Not via form/chat. Sensitive handling is discussed securely if required."
              />
              <FAQItem 
                question="How long does it take?"
                answer="Hours to days depending on complexity. Typo correction: typically hours. Full permutation: days to weeks."
              />
              <FAQItem 
                question="What if it is a passphrase issue?"
                answer="Passphrase recovery is assessed separately as a distinct service category."
              />
              <FAQItem 
                question="Is it safe to try offline tools?"
                answer="Only if you fully trust the tool/environment. We recommend professional assessment first to avoid data exposure."
              />
              <FAQItem 
                question="Can you recover if order is completely unknown?"
                answer="Sometimes, but complexity can be extreme. Feasibility assessment determines if computational requirements are reasonable relative to wallet value."
              />
            </div>
          </section>

          {/* CTA */}
          <section className="cta-section" style={{marginTop: '80px', padding: '60px 48px'}}>
            <h2 className="cta-title" style={{fontSize: '36px'}}>
              Seed Phrase Typos or Wrong Order?
            </h2>
            <p className="cta-subtitle" style={{marginBottom: '40px'}}>
              Request a free, confidential assessment
            </p>
            <button className="btn-primary" style={{fontSize: '18px', padding: '18px 40px'}} onClick={() => window.location.href = '/#assessment'}>
              START FREE ASSESSMENT
            </button>
            <p className="hero-small-text" style={{marginTop: '24px'}}>
              Non-custodial • Confidential • Professional
            </p>
            <div style={{marginTop: '32px'}}>
              <p style={{color: 'var(--grigio-chiaro)', marginBottom: '12px'}}>
                <strong>Related Services:</strong>
              </p>
              <a href="/seed-phrase-recovery" className="card-link" style={{fontSize: '16px', marginRight: '20px'}}>
                Seed Phrase Recovery Service →
              </a>
              <a href="/resources/seed-phrase-missing-1-word" className="card-link" style={{fontSize: '16px'}}>
                Missing 1 Word Guide →
              </a>
            </div>
          </section>

          {/* Breadcrumb Back */}
          <div style={{marginTop: '60px', textAlign: 'center'}}>
            <a href="/resources" className="card-link" style={{fontSize: '16px'}}>
              ← Back to All Resources
            </a>
          </div>

        </div>
      </article>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-bottom">
            <p className="footer-copyright">
              © 2025-2026 ARCANA CRYPTO LTD - All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SeedPhraseTyposWrongOrder;
